/**
 * Email Template & Automation Service
 * AIDLC Administrative Frontend
 *
 * API calls for email templates, automation rules, and sent email log.
 */

import apiClient from './api';
import type {
  EmailTemplate,
  EmailAutomationRule,
  SentEmailRecord,
  TemplateVariable,
} from '../types/email';

const BASE = '/email/templates';

// ─── Email Templates ─────────────────────────────────────────────

export async function getTemplates(): Promise<EmailTemplate[]> {
  const response = await apiClient.get<EmailTemplate[]>(BASE);
  return response.data;
}

export async function getTemplate(id: string): Promise<EmailTemplate> {
  const response = await apiClient.get<EmailTemplate>(`${BASE}/${id}`);
  return response.data;
}

export async function createTemplate(
  data: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt' | 'variables' | 'createdBy'>
): Promise<EmailTemplate> {
  const response = await apiClient.post<EmailTemplate>(BASE, data);
  return response.data;
}

export async function updateTemplate(
  id: string,
  updates: Partial<EmailTemplate>
): Promise<EmailTemplate> {
  const response = await apiClient.put<EmailTemplate>(`${BASE}/${id}`, updates);
  return response.data;
}

export async function deleteTemplate(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/${id}`);
}

export async function previewTemplate(
  id: string,
  data?: { agentId?: string; variables?: Record<string, string> }
): Promise<{ subject: string; body: string; variables: Record<string, string> }> {
  const response = await apiClient.post(`${BASE}/${id}/preview`, data || {});
  return response.data;
}

export async function getTemplateVariables(): Promise<TemplateVariable[]> {
  const response = await apiClient.get<TemplateVariable[]>(`${BASE}/variables`);
  return response.data;
}

// ─── Automation Rules ────────────────────────────────────────────

export async function getAutomationRules(): Promise<EmailAutomationRule[]> {
  const response = await apiClient.get<EmailAutomationRule[]>(`${BASE}/automation`);
  return response.data;
}

export async function createAutomationRule(
  data: Omit<EmailAutomationRule, 'id' | 'createdAt' | 'updatedAt' | 'totalSent' | 'lastTriggeredAt' | 'createdBy'>
): Promise<EmailAutomationRule> {
  const response = await apiClient.post<EmailAutomationRule>(`${BASE}/automation`, data);
  return response.data;
}

export async function updateAutomationRule(
  id: string,
  updates: Partial<EmailAutomationRule>
): Promise<EmailAutomationRule> {
  const response = await apiClient.put<EmailAutomationRule>(`${BASE}/automation/${id}`, updates);
  return response.data;
}

export async function deleteAutomationRule(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/automation/${id}`);
}

export async function toggleAutomationRule(id: string): Promise<EmailAutomationRule> {
  const response = await apiClient.post<EmailAutomationRule>(`${BASE}/automation/${id}/toggle`);
  return response.data;
}

export async function testAutomationRule(
  id: string,
  variables?: Record<string, string>
): Promise<{ rule: { id: string; name: string }; preview: { subject: string; body: string }; variables: Record<string, string>; wouldSendTo: string[] | string }> {
  const response = await apiClient.post(`${BASE}/automation/${id}/test`, { variables });
  return response.data;
}

// ─── Sent Email Log + Agent Email ────────────────────────────────

export async function getSentEmails(agentId?: string): Promise<SentEmailRecord[]> {
  const response = await apiClient.get<SentEmailRecord[]>('/email/sent', {
    params: agentId ? { agentId } : undefined,
  });
  return response.data;
}

export async function sendAgentEmail(data: {
  to: string;
  subject: string;
  body: string;
  agentId: string;
  templateId?: string;
}): Promise<{ success: boolean; to: string; subject: string }> {
  const response = await apiClient.post('/email/send', data);
  return response.data;
}
